/**
 * POSReceiptDialog — post-sale receipt/print/send dialog.
 * Extracted from POSPage.js to reduce component size.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  smartPrintReceipt, openCashDrawerSafe,
} from '../../lib/hardwareService';
import { printThermalReceipt, printProfessionalInvoice } from '../../lib/invoiceUtils';
import {
  Receipt, CloudOff, Usb, Printer, FileText,
  Mail, Loader2, MessageSquare, Sparkles, DoorOpen,
} from 'lucide-react';
import { fmtCurrency, fmtNumber } from '../../lib/localeUtils';
import { keyed } from '../../lib/listKeys';

export function POSReceiptDialog({
  open,
  onOpenChange,
  sale,
  printerConnected,
  emailingReceipt,
  customerEmail,
  onEmailReceipt,
  smsingReceipt,
  customerPhone,
  onSmsReceipt,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Sale Complete
          </DialogTitle>
        </DialogHeader>
        {sale && (
          <div className="space-y-4">
            <div className="text-center border-b pb-3">
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>DutiLyne</h3>
              <p className="text-xs text-muted-foreground">Invoice #{sale.sale_id}</p>
              <p className="text-xs text-muted-foreground">{new Date(sale.created_at).toLocaleString()}</p>
              {sale.is_offline && (
                <Badge variant="secondary" className="mt-1 text-[10px] gap-1">
                  <CloudOff className="h-3 w-3" /> Offline — will sync later
                </Badge>
              )}
            </div>
            {sale.customer_name && (
              <p className="text-sm">Customer: <strong>{sale.customer_name}</strong></p>
            )}
            <div className="space-y-2">
              {keyed(sale.items, item => item.product_name).map(({ item, key }) => (
                <div key={key} className="flex justify-between text-sm">
                  <span>{item.product_name} x{item.quantity}</span>
                  <span>{fmtCurrency((item.quantity * item.unit_price))}</span>
                </div>
              ))}
            </div>
            <Separator />
            <div className="space-y-1">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>{fmtCurrency(sale.subtotal)}</span></div>
              {sale.discount > 0 && <div className="flex justify-between text-sm text-destructive"><span>Discount</span><span>-{fmtCurrency(sale.discount)}</span></div>}
              {sale.loyalty_discount > 0 && (
                <div className="flex justify-between text-sm text-emerald-700" data-testid="receipt-loyalty-line">
                  <span>Loyalty ({fmtNumber(sale.loyalty_redeem_points || 0)} pts)</span>
                  <span>-{fmtCurrency(sale.loyalty_discount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{fmtCurrency(sale.total)}</span></div>
              <p className="text-xs text-muted-foreground capitalize">Payment: {sale.payment_method}</p>
              {sale.loyalty_summary && (
                <div className="mt-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-2" data-testid="receipt-loyalty-summary">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>
                    Earned <b>+{fmtNumber(sale.loyalty_summary.points_earned || 0)}</b> pts ·
                    Balance <b>{fmtNumber(sale.loyalty_summary.balance || 0)}</b> ·
                    Tier <b>{sale.loyalty_summary.tier}</b>
                  </span>
                </div>
              )}
            </div>
            {/* Print Options */}
            <div className="space-y-2 pt-2">
              <div className="flex gap-2">
                {printerConnected ? (
                  <Button
                    variant="default"
                    className="flex-1 gap-1.5"
                    onClick={() => smartPrintReceipt(sale).then(r => toast.success(r.fallback ? 'Printed (browser fallback)' : 'Receipt sent to printer')).catch(() => toast.error('Print failed'))}
                    data-testid="print-escpos-btn"
                  >
                    <Usb className="h-4 w-4" /> Print Receipt (Epson)
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1 gap-1.5"
                    onClick={() => printThermalReceipt(sale)}
                    data-testid="print-thermal-receipt-btn"
                  >
                    <Printer className="h-4 w-4" /> Thermal Receipt
                  </Button>
                )}
                <Button
                  className="flex-1 gap-1.5"
                  onClick={() => printProfessionalInvoice(sale)}
                  data-testid="print-invoice-btn"
                >
                  <FileText className="h-4 w-4" /> Full Invoice
                </Button>
              </div>
              {/* Email receipt */}
              {!sale.is_offline && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 text-xs"
                  disabled={emailingReceipt}
                  onClick={() => {
                    const onFile = (sale.customer_email || customerEmail || '').trim();
                    const target = onFile || window.prompt('Email receipt to:', '');
                    if (target) onEmailReceipt(sale, target);
                  }}
                  data-testid="email-receipt-btn"
                >
                  {emailingReceipt
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending...</>
                    : <><Mail className="h-3.5 w-3.5" /> Email Receipt{sale.customer_email ? ` to ${sale.customer_email}` : ''}</>
                  }
                </Button>
              )}
              {/* SMS receipt */}
              {!sale.is_offline && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 text-xs"
                  disabled={smsingReceipt}
                  onClick={() => {
                    const onFile = (sale.customer_phone || customerPhone || '').trim();
                    const target = onFile || window.prompt('SMS receipt to (Ghana phone):', '');
                    if (target) onSmsReceipt(sale, target);
                  }}
                  data-testid="sms-receipt-btn"
                >
                  {smsingReceipt
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending SMS...</>
                    : <><MessageSquare className="h-3.5 w-3.5" /> Text Receipt{sale.customer_phone ? ` to ${sale.customer_phone}` : ''}</>
                  }
                </Button>
              )}
              {printerConnected && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 text-xs"
                  onClick={() => openCashDrawerSafe().then(ok => ok ? toast.success('Drawer opened') : null)}
                  data-testid="receipt-open-drawer-btn"
                >
                  <DoorOpen className="h-3.5 w-3.5" /> Open Cash Drawer
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => onOpenChange(false)}
              data-testid="close-receipt-btn"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
